import { EVENT_COLORS } from './constants';

// ── Date formatting ───────────────────────────────────────────────────

/** Short date: "Jan 5, 2025" */
export function formatDateShort(date: string | null | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Date with time: "Jan 5, 2025, 14:30" */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Timestamp: "Jan 5, 14:30:22" */
export function formatTimestamp(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** Relative time: "5m ago", "2h ago", "3d ago" */
export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Byte formatting ───────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function formatStorageSize(n: number): string {
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

// ── Event helpers ─────────────────────────────────────────────────────

export function getEventColor(type: string): string {
  if (EVENT_COLORS[type]) return EVENT_COLORS[type];
  if (type.includes('error') || type.includes('fail')) return 'bg-red-500';
  if (type.includes('creat')) return 'bg-emerald-500';
  if (type.includes('updat')) return 'bg-blue-500';
  if (type.includes('delet')) return 'bg-red-500';
  return 'bg-gray-400';
}

export interface ActivityEvent {
  id: string;
  eventType: string;
  eventData: Record<string, unknown>;
  createdAt: string;
  userId?: string | null;
}

export function formatEventLabel(event: ActivityEvent): string {
  const d = event.eventData || {};
  switch (event.eventType) {
    case 'site_deployed':
      return `Deployed site ${(d.subdomain as string) || ''}`;
    case 'rule_created':
      return `Created rule for ${(d.entity_name as string) || 'entity'}${d.rule_type ? ` (${d.rule_type})` : ''}`;
    case 'login':
      return `User login via ${(d.method as string) || 'password'}`;
    case 'signup':
      return 'New user signed up';
    default:
      return event.eventType.replace(/[._]/g, ' ');
  }
}

// ── URL masking ───────────────────────────────────────────────────────

export function maskUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.hostname}/***`;
  } catch {
    return '***';
  }
}

// ── Miscellaneous ─────────────────────────────────────────────────────

export function isExpired(iso: string): boolean {
  return new Date(iso) < new Date();
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/** Validate a return-to URL is safe (starts with / and not //) */
export function sanitizeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
}

/** Extract initials from a name: "John Doe" → "JD" */
export function getInitials(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ── Navigation helpers ────────────────────────────────────────────────

const HIDDEN_NAV_PREFIXES = ['/login', '/oauth/', '/install/', '/invite/'];
const HIDDEN_NAV_EXACT = ['/', '/onboarding', '/privacy', '/terms', '/cookies'];

/** Pages where the nav shell should be hidden */
export function shouldShowNav(pathname: string, isAuthenticated: boolean): boolean {
  if (!isAuthenticated) return false;
  if (HIDDEN_NAV_EXACT.includes(pathname)) return false;
  return !HIDDEN_NAV_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Check if a nav item href is active for the current pathname */
export function isNavActive(href: string, pathname: string): boolean {
  if (href === '/entities') {
    return pathname === '/entities' || (pathname.startsWith('/entities/') && !pathname.startsWith('/entities/settings'));
  }
  return pathname === href || pathname.startsWith(href + '/');
}
